import React, { useState, useEffect } from 'react';
import { factoriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Factory, MapPin, User, Phone, Mail, Package, Clock, CheckCircle2, 
  Edit2, Trash2, Loader2
} from 'lucide-react';

const Factories = () => {
  const { isProduction, isAdmin } = useAuth();
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    contact_person: '',
    phone: '',
    email: '',
    capacity: '',
    notes: '',
    notification_emails_text: ''  // comma or newline separated in the UI
  });

  useEffect(() => {
    fetchFactories();
  }, []);

  const fetchFactories = async () => {
    setLoading(true);
    try {
      const response = await factoriesApi.getAll();
      setFactories(response.data);
    } catch (error) {
      toast.error('Failed to load factories');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      contact_person: '',
      phone: '',
      email: '',
      capacity: '',
      notes: '',
      notification_emails_text: ''
    });
  };

  // Parse the comma/newline separated textarea into a clean list of unique emails.
  const parseEmails = (raw) => Array.from(new Set(
    (raw || '')
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
  ));

  const handleCreate = async () => {
    if (!formData.name || !formData.location) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const { notification_emails_text, ...rest } = formData;
      await factoriesApi.create({
        ...rest,
        capacity: parseInt(formData.capacity) || 0,
        notification_emails: parseEmails(notification_emails_text)
      });
      toast.success('Factory created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchFactories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create factory');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (factory) => {
    setEditingFactory(factory);
    setFormData({
      name: factory.name,
      location: factory.location,
      contact_person: factory.contact_person,
      phone: factory.phone,
      email: factory.email,
      capacity: factory.capacity.toString(),
      notes: factory.notes,
      notification_emails_text: (factory.notification_emails || []).join('\n')
    });
    setIsEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.location) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const { notification_emails_text, ...rest } = formData;
      await factoriesApi.update(editingFactory.id, {
        ...rest,
        capacity: parseInt(formData.capacity) || 0,
        notification_emails: parseEmails(notification_emails_text)
      });
      toast.success('Factory updated successfully');
      setIsEditOpen(false);
      setEditingFactory(null);
      resetForm();
      fetchFactories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update factory');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (factoryId) => {
    if (!window.confirm('Are you sure you want to delete this factory?')) return;

    try {
      await factoriesApi.delete(factoryId);
      toast.success('Factory deleted');
      fetchFactories();
    } catch (error) {
      toast.error('Failed to delete factory');
    }
  };

  const FactoryForm = ({ onSubmit, submitText }) => (
    <div className="grid grid-cols-2 gap-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="name">Factory Name *</Label>
        <Input
          id="name"
          data-testid="factory-name-input"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder="Enter factory name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location *</Label>
        <Input
          id="location"
          data-testid="factory-location-input"
          value={formData.location}
          onChange={(e) => setFormData({...formData, location: e.target.value})}
          placeholder="Enter location"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person">Contact Person</Label>
        <Input
          id="contact_person"
          data-testid="factory-contact-input"
          value={formData.contact_person}
          onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
          placeholder="Enter contact name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          data-testid="factory-phone-input"
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          placeholder="Enter phone number"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          data-testid="factory-email-input"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          placeholder="Enter email"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="capacity">Monthly Capacity</Label>
        <Input
          id="capacity"
          type="number"
          data-testid="factory-capacity-input"
          value={formData.capacity}
          onChange={(e) => setFormData({...formData, capacity: e.target.value})}
          placeholder="Enter capacity"
        />
      </div>
      <div className="col-span-2 space-y-2">
        <Label htmlFor="notification_emails">
          Notification Emails
          <span className="ml-1 text-xs text-muted-foreground font-normal">
            (comma or new-line separated — receives file uploads)
          </span>
        </Label>
        <Textarea
          id="notification_emails"
          data-testid="factory-notification-emails-input"
          value={formData.notification_emails_text}
          onChange={(e) => setFormData({ ...formData, notification_emails_text: e.target.value })}
          placeholder="ops@factory.com, mayur@serenity.com"
          rows={2}
        />
        <p className="text-[11px] text-muted-foreground">
          Whenever a Manufacturing Order Form or Factory Processing Sheet is uploaded for an order assigned to this factory, an email with the file attached is sent to these addresses.
        </p>
      </div>
      <div className="col-span-2 space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          data-testid="factory-notes-input"
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Additional notes..."
          rows={3}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header title="Factories" />
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="factories-page">
      <Header title="Factories" onRefresh={fetchFactories}>
        {isProduction() && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20"
                data-testid="create-factory-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Factory
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Factory</DialogTitle>
                <DialogDescription>Add a new manufacturing partner</DialogDescription>
              </DialogHeader>
              <FactoryForm />
              <DialogFooter>
                <Button variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreate}
                  data-testid="submit-factory-btn"
                  className="bg-amber-500 hover:bg-amber-600"
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add Factory
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </Header>

      <div className="p-6">
        {factories.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Factory className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-2">
                No factories yet
              </h3>
              <p className="text-slate-500 mb-4">
                Add your first manufacturing partner to get started
              </p>
              {isProduction() && (
                <Button 
                  onClick={() => setIsCreateOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Factory
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {factories.map(factory => (
              <Card 
                key={factory.id} 
                className="hover:shadow-md transition-all duration-200 hover:border-amber-500/30"
                data-testid={`factory-card-${factory.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                        <Factory className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-lg">{factory.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {factory.location}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant={factory.is_active ? "default" : "secondary"}>
                      {factory.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Contact Info */}
                  <div className="space-y-2 text-sm">
                    {factory.contact_person && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <User className="h-4 w-4" />
                        <span>{factory.contact_person}</span>
                      </div>
                    )}
                    {factory.phone && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Phone className="h-4 w-4" />
                        <span>{factory.phone}</span>
                      </div>
                    )}
                    {factory.email && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{factory.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-neutral-900">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <Package className="h-4 w-4" />
                        <span className="text-xs">Total Orders</span>
                      </div>
                      <p className="font-bold text-lg">{factory.total_orders}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-neutral-900">
                      <div className="flex items-center gap-2 text-slate-500 mb-1">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-xs">Completed</span>
                      </div>
                      <p className="font-bold text-lg">{factory.completed_orders}</p>
                    </div>
                  </div>

                  {/* Performance */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">On-Time Delivery</span>
                      <span className="font-semibold text-emerald-600">{factory.on_time_delivery_pct}%</span>
                    </div>
                    <Progress value={factory.on_time_delivery_pct} className="h-2" />
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-slate-500">
                      <Clock className="h-4 w-4" />
                      <span>Avg. Turnaround</span>
                    </div>
                    <span className="font-medium">{factory.avg_turnaround_days} days</span>
                  </div>

                  {/* Actions */}
                  {isProduction() && (
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleEdit(factory)}
                        data-testid={`edit-factory-${factory.id}`}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      {isAdmin() && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(factory.id)}
                          data-testid={`delete-factory-${factory.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { setIsEditOpen(open); if (!open) { setEditingFactory(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Factory</DialogTitle>
            <DialogDescription>Update factory details</DialogDescription>
          </DialogHeader>
          <FactoryForm />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditOpen(false); setEditingFactory(null); resetForm(); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdate}
              data-testid="update-factory-btn"
              className="bg-amber-500 hover:bg-amber-600"
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Factory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Factories;
